import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";

type CategoriesSearch = {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  filters?: FilterConditionDTO[];
};

type CategoriesFilterProps = {
  /** ค่าค้นหาปัจจุบัน (จาก URL) — ใช้กรองต้นไม้ฝั่ง client */
  onSearchChange?: (q: string) => void;
};

export function CategoriesFilter({ onSearchChange }: CategoriesFilterProps) {
  const nav = useNavigate({ from: "/app/categories" });
  const search = useSearch({ from: "/app/categories" }) as CategoriesSearch;

  const filters = search.filters ?? [];
  const nameFilter = findCondition(filters, "name");
  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) ?? "",
  );

  useEffect(() => {
    const v = (nameFilter?.value as string) ?? "";
    setSearchValue(v);
    onSearchChange?.(v);
  }, [nameFilter?.value, onSearchChange]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    onSearchChange?.(val);
    let next = removeConditions(filters, "name");
    if (val.trim()) {
      next = upsertCondition(next ?? [], {
        field: "name",
        op: "contains",
        value: val.trim(),
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  }, 300);

  const hasFilter = Boolean(searchValue.trim());

  return (
    <div className="mb-2 flex items-center justify-between gap-4 px-2">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <Input
          placeholder="ຄົ້ນຫາຕາມຊື່ຫມວດຫມູ່..."
          value={searchValue}
          onChange={(e) => {
            const v = e.target.value;
            setSearchValue(v);
            debounced(v);
          }}
          className="h-8 sm:max-w-2xs"
        />
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearchValue("");
              onSearchChange?.("");
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: undefined,
                },
              });
            }}
          >
            ລ້າງຕົວກອງ
          </Button>
        )}
      </div>
    </div>
  );
}
